import type { BoardColumn } from "~/shared/types/quote-board-types";

import { gridColumnVisibilityEnums } from "./grid-identifier-enums.ts";

export type GridPrefColumn = {
	columnId: string;
	isVisible: boolean;
	widthSize: number;
	ordinalPosition: number;
	visibility: string;
};

/**
 * Minimum column width (px) enforced by `applyGridPrefs`. Guards against
 * corrupt or stale pref records that would otherwise zero-width a column.
 */
export const MIN_COLUMN_WIDTH = 40;

const VISIBILITY_INTERNAL = gridColumnVisibilityEnums[1];
const VISIBILITY_EXTERNAL = gridColumnVisibilityEnums[2];
export const VISIBILITY_ALL = gridColumnVisibilityEnums[3];

/** Input column shape — `show` may be omitted; `applyGridPrefs` guarantees it on output. */
type GridPrefInputColumn = Omit<BoardColumn, "show"> & { show?: boolean };

/** Icon-style column with no `field` (e.g. tracking, document icons). */
const isIconColumn = (col: GridPrefInputColumn) => !col.field;

const isPinned = (col: GridPrefInputColumn) =>
	isIconColumn(col) || col.required === true;

export const applyGridPrefs = <T extends GridPrefInputColumn>(
	columns: T[],
	prefs: GridPrefColumn[],
	isInternal: boolean,
): (T & { show: boolean })[] => {
	const prefsByField = new Map(prefs.map((p) => [p.columnId, p]));
	const otherDeploymentLabel = isInternal
		? VISIBILITY_EXTERNAL
		: VISIBILITY_INTERNAL;

	const fixed = columns.filter((col) => isPinned(col));

	const data = columns
		.filter((col) => !isPinned(col))
		.filter((col) => {
			const pref = prefsByField.get(col.field ?? "");
			return !pref || pref.visibility !== otherDeploymentLabel;
		})
		.map((col) => {
			const pref = prefsByField.get(col.field ?? "");
			if (!pref) return col;

			const show = col.required ? true : pref.isVisible;

			const rawWidth = Number(pref.widthSize);
			const width = Number.isFinite(rawWidth)
				? Math.max(MIN_COLUMN_WIDTH, rawWidth)
				: MIN_COLUMN_WIDTH;

			return { ...col, show, width };
		})
		.sort((a, b) => {
			const aPos =
				prefsByField.get(a.field ?? "")?.ordinalPosition ??
				Number.MAX_SAFE_INTEGER;
			const bPos =
				prefsByField.get(b.field ?? "")?.ordinalPosition ??
				Number.MAX_SAFE_INTEGER;
			return aPos - bPos;
		});

	const ordered = [...fixed, ...data];
	// Guarantee `show: boolean` on every output column. Pinned/unmatched
	// columns default to visible; pref-matched columns already carry the
	// resolved `show` from the map above (and override this default).
	return ordered.map((col, index) => ({
		show: true,
		...col,
		orderIndex: index,
	}));
};
